import _get from 'lodash/get'
import store from '@ps/store'
import {
  generateUUID,
  DFS,
  file2lang,
  addPluginSetting,
  deletePluginSetting,
} from '@ps/utils/game'
import { AssetType, AssetRoute, GamePage } from '@ps/utils/config'
import {
  checkNodeRes,
  cooperationMerge,
  cooperationUpdateVersion,
  fixMissNodeRes,
  recoverNodeRes,
} from '@ps/utils/cooperation'
import { fileSave, saveFile, readFile, deleteFile } from '@ps/api/editor/file'
import { EventBus, Event } from '@ps/utils/event-bus'
import prefab from '../workbench/prefab'
import screenAdapter from './screen-adapter'
import device from '../workbench/device'
import scriptManager from './script-manager'
import sequenceManager from '../workbench/sequence'
import sceneManager from '../workbench/scene'
import visualProgram from '../workbench/visual-program'
import drawLine from '../workbench/draw-line'
import erase from '../workbench/erase'
import templateVersion from './template-version'
import router from '@ps/router'
import actions from '../workbench/game/actions/index'
import video from '../workbench/video/index'
import audio from '../workbench/audio'
import takeColor from '../workbench/take-color'
import bgm from '../workbench/bgm'
import mutations from '../workbench/game/mutations/index'
import {
  adaptNonNormMaterialDataWhenGameReady,
  featureDetect,
} from '../../workbench/adapter'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { specialNodes } from '@ps/bridge/qc/node/config'
import {
  isDragBound,
  isDragTargetArea,
  isFreeDrawLineNode,
  isLangImage,
  isSequenceFrame,
} from '@ps/bridge/qc/base'
import _ from 'lodash'
import * as bgScript from '@ps/bridge/qc/scripts/bg-config'
import filePath from '@ps/bridge/path'
import gameIframe from '@ps/bridge/iframe/iframe'
import pathConfig from '@ps/bridge/path'
import {
  getBgImgBinUrl,
  getBgImgUrl,
  getBgmAudioUrl,
} from '@ps/workbench/global-config'
import {
  convertLanguagesConfigWhenGameReady,
  fixLanguagesWhenGameReady,
  fixLangConfigForSelfDev,
  fixNodeForSelfDev,
} from '@ps/workbench/post-processor'
import { getCurrentActionBinFiles } from '@ps/bridge/qc/scripts/movie-clip'
import {
  hasSelfDevPermission,
  isCooperateProject,
} from '@ps/permission/workbench'
import {
  isRigidBody,
  getRigidBodyType,
  DEFAULT_BOX2D_PRECISION,
  psRemoveBox2dBody,
} from '@ps/workbench/undo-redo/module/box2d'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { minimizeLayout } from '@ps/bridge/qc/scripts/layout'
import { eraseToInit } from '@ps/workbench/undo-redo/module/erase'
import { maskSaveHandler } from '@ps/workbench/undo-redo/module/mask'
import { carouselSaveHandler } from '@ps/bridge/qc/node/carousel'
import { scripts } from '@ps/bridge/qc/scripts'
import { Message } from 'element-ui'
import {
  cutTimelineRealAudio,
  cutTimelineRealVideo,
} from '../interactive-video/timeline/timeline'
import { isIvPage } from '@ps/router/config'
import { getFinalBrain } from '../interactive-video/brain/brain'
import {
  validateIvData,
  validateSceneBrain,
} from '../interactive-video/common/utils'

function detect(iframeWindow) {
  try {
    featureDetect(iframeWindow)
    return true
  } catch (err) {
    console.error(
      `特性检测失败====================，不对包进行转换操作：${err.message}`,
    )
    return false
  }
}

const game = {
  namespaced: true,

  state: {
    data: null, // iframe 的 qc 对象
    onLabelEditing: false,
    uuid2url: null, // uuid到url的映射表
    currentPage: GamePage.GAME,
    game: null,
    gameReady: false,
    UIRoot: null, // UIRoot节点
    gamePlay: null, // gamePlay节点
    iframeWindow: null,
    globalUrlMap: pathConfig.GLOBAL_URL_MAP,
    // 当前节点
    // 当需要获取节点的索引的时候，不要使用 activeNode，要使用 activeNode 原始的节点。因为 activeNode 是一个 proxy 对象，跟原始的节点对象不一样。
    activeNode: null, // 当前场景树中选中的节点
    mirrorNode: {}, // 镜像节点
    activeNodeList: [],

    // 被锁定的节点id列表
    lockedNodeUuids: [],
    lockEditAll: false, // 锁定所有操作标志,目前用于拖曳节点编辑时使用
    availableNodeWhenLockEdit: [], // 锁定所有操作时可操作的节点,用于编辑拖曳节点
    previewBox2dShapeState: false, // 是否显示box2d轮廓的状态
    previewBox2dShapeAllState: false, // 是否显示全部box2d轮廓的状态
    firstComponentTip: false, // 添加2d玩法组件时弹出的动画提示
    firstQuickLayoutTip: false, // 棋盘、停车、拼图等快捷布局添加组件时弹出的动画提示
  },

  getters: {
    gamePlay(state) {
      return state.gamePlay
    },

    /**
     * @description 查找当前场景的所有子节点
     * @param {*} state
     * @returns 当前场景的所有子节点
     */
    currentSceneTree(state) {
      if (!state.data) return []
      return _get(state, 'sceneManager.currentSceneNode.children') || []
    },
    /**
     * @description 查找当前场景的所有子节点包括场景节点
     * @param {*} state
     * @returns 当前场景的所有子节点包括场景节点
     */
    currentSceneTreeIncludeScene(state) {
      if (!state.data) return []
      return [_get(state, 'sceneManager.currentSceneNode')]
    },
  },

  mutations: {
    toggleLabelEditing(state, { val }) {
      state.onLabelEditing = val
    },

    updateFirstQuickLayoutTip(state, value) {
      state.firstQuickLayoutTip = value
    },

    ...mutations,
  },

  actions: {
    async initGame({ state, commit, rootState }, { data, iframeWindow }) {
      const { gameOb } = data
      state.uuid2url = gameOb.assets._uuid2UrlConf
      state.game = gameOb
      state.UIRoot = iframeWindow.UIRoot
      state.data = data
      state.iframeWindow = iframeWindow
      state.gamePlay = iframeWindow.UIRoot.getChild('gamePlay')

      const isNormMaterial = store.state.workspace.project.isNormMaterial
      const gamePlayNode = iframeWindow.UIRoot.getChild(
        specialNodes.gamePlay.name,
      )
      const gamePlayNodeHasEditorScript = Boolean(
        editorScript.getScript(gamePlayNode, false),
      )
      const languagesRes = iframeWindow.languagesMgr.getRes()
      let isAdaptedNonNormMaterial = false

      // 转换文本配置格式
      convertLanguagesConfigWhenGameReady({
        languagesRes,
        iframeWindow,
      })

      // 标准包
      if (isNormMaterial) {
        if (gamePlayNodeHasEditorScript) {
          console.log('标准包，gamePlay 节点有 editor.data 脚本')
        } else {
          console.log(
            '标准包，gamePlay 节点没有 editor.data 脚本，一般不会出现这种情况。如果有的话，说明包有问题，请检查包',
          )
        }
      } else {
        if (gamePlayNodeHasEditorScript) {
          // 素材包的 playsmart.editor.data 脚本跟 ps 的不一样，但是 ps 这边 playsmart.editor.data 脚本后加载，所以会重写素材包的 playsmart.editor.data 脚本
          const gamePlayNodeEditorScript = editorScript.getScript(
            gamePlayNode,
            false,
          )
          isAdaptedNonNormMaterial = gamePlayNodeEditorScript.$data.isAdapted
          if (isAdaptedNonNormMaterial) {
            console.log('非标准包，gamePlay 节点有 editor.data 脚本且适配过')
          } else {
            console.log('非标准包，gamePlay 节点有 editor.data 脚本，未适配过')
            if (detect(iframeWindow)) {
              await adaptNonNormMaterialDataWhenGameReady({
                iframeWindow,
                qc: data,
                pid: store.state.workspace.pid,
              })
            }
          }
        } else {
          console.log('非标准包，gamePlay 节点没有 editor.data 脚本，未适配过')
          if (detect(iframeWindow)) {
            await adaptNonNormMaterialDataWhenGameReady({
              iframeWindow,
              qc: data,
              pid: store.state.workspace.pid,
            })
          }
        }
      }
      // 自研素材初始化
      if (hasSelfDevPermission()) {
        fixLangConfigForSelfDev({ iframeWindow })
        fixNodeForSelfDev({ iframeWindow })
      }
      // 标准包与转换过的非标准包
      if (isNormMaterial || (!isNormMaterial && isAdaptedNonNormMaterial)) {
        fixLanguagesWhenGameReady({
          iframeWindow,
          qc: data,
        })
        // 检测是否有丢失资源，有则补回
        fixMissNodeRes()
      }
      state.gameReady = true
      EventBus.$emit(Event.INITGAME)
      // 互动视频调试环境校验数据正确性
      if (isIvPage() && Number(router.currentRoute.query.debug) === 1) {
        validateIvData()
      }
    },

    async saveScene({ rootState, state }) {
      const start = performance.now()
      let hasCoMerge
      // 先检测是否有图片和粒子节点丢失贴图资源
      const { res, nodeUuid2Res, errTip } = checkNodeRes()
      if (hasSelfDevPermission()) {
        if (!res) {
          Message({
            message: errTip,
            type: 'error',
          })
          return
        }
        // 协同流程项目合并研发的版本
        hasCoMerge = await cooperationMerge()
        // 合并后还原图片和粒子节点丢失的贴图资源(如有)
        recoverNodeRes()
      }
      const projectKey = router.currentRoute.query.id
      // box2d物理引擎
      // 关闭刚体轮廓
      store.commit('workspace/game/updatePreviewBox2dShapeState', {
        val: false,
      })
      store.commit('workspace/game/updatePreviewBox2dShapeAllState', {
        val: false,
      })
      const gamePlay = gameIframe.gamePlay
      const nodes = getDescendants(gamePlay)
      let hasRigidBody = false
      for (const i in nodes) {
        const node = nodes[i]
        const ped = node.getScript('playsmart.editor.data')
        if (isDragTargetArea(node) || isDragBound(node)) {
          node.visible = true
        }
        if (ped && ped.$data && ped.$data.box2d) {
          if (isRigidBody(node)) {
            hasRigidBody = true
            if (!isFreeDrawLineNode(node)) {
              let box2dScript = node.getScript('qc.Box2D.Body')
              if (!box2dScript) {
                box2dScript = node.addScript('qc.Box2D.Body')
              }
              box2dScript.type = getRigidBodyType(node)
              psRemoveBox2dBody(node)
              box2dScript.isAwake = true
              if (node.texture) {
                let precision = DEFAULT_BOX2D_PRECISION
                if (ped.$data.box2d.precision) {
                  precision = ped.$data.box2d.precision
                }
                box2dScript.resetShapeFromEdge(precision)
              }
            }
          } else {
            if (gameIframe.qc.Box2D && gameIframe.qc.Box2D.Body) {
              const box2dScript = node.getScript('qc.Box2D.Body')
              if (box2dScript) {
                node.removeScript(box2dScript)
              }
            }
          }
        }
        // 调整滤镜会把之前的图片也带进去allFramePlaySmart里，导致把所有图片导出。需要做精简，和动作的保持一致
        if (isSequenceFrame(node)) {
          const curFrame = getCurrentActionBinFiles(node)
          const mc = node.getScript(scripts.movieClip.name)
          mc.data.allFramePlaySmart = curFrame
        }
        // 某些中间操作会出现layout中存在各种奇怪的key，保存时兜底过滤掉这些key
        const Layout = node.getScript('ps.Layout')
        if (Layout) {
          const layout = Layout.layout
          for (const i in layout) {
            if (
              layout[i] &&
              ['merge', 'clone', 'layoutPriority', 'keys'].some(
                (item) => layout[i].indexOf(item) > -1,
              )
            ) {
              const data = layout[i].split(',')
              const ans = data.filter(
                (item) =>
                  item.indexOf('merge') === -1 &&
                  item.indexOf('clone') === -1 &&
                  item.indexOf('layoutPriority') === -1 &&
                  item.indexOf('keys') === -1,
              )
              Layout.layout[i] = ans.join(',')
            }
          }
        }
        minimizeLayout(node)
      }
      if (hasRigidBody) {
        await addPluginSetting('com.qici.box2d')
      } else {
        // 协同的项目由研发控制删减box2d,因为研发可以通过代码动态添加box2d
        if (!isCooperateProject()) {
          await deletePluginSetting('com.qici.box2d')
        }
      }
      // 确保拖拽指定区域和拖拽范围层级在最顶上
      gamePlay.children.forEach((scene) => {
        const len = scene.children.length
        scene.children.forEach((child) => {
          if (isDragBound(child)) {
            scene.setChildIndex(child, len - 1)
          }
        })
        scene.children.forEach((child) => {
          if (isDragTargetArea(child)) {
            scene.setChildIndex(child, len - 1)
          }
        })
      })
      // 保存时对2d玩玩法组件内的节点后置操作
      eraseToInit(nodes)
      maskSaveHandler(nodes)
      carouselSaveHandler(nodes)
      // 互动视频项目裁剪视频和音频
      if (isIvPage()) {
        await cutTimelineRealVideo()
        await cutTimelineRealAudio()
        validateSceneBrain()
      }
      // 导出时必须的资源白名单
      const necessaryRes = [getBgImgBinUrl(), getBgImgUrl()]
      const dependences = {}
      const qcGame = gameIframe.qcGame
      const world = qcGame.world
      const content = qcGame.serializer.buildBundle(world, dependences)
      content.dependences = qcGame.serializer.combineDependence(dependences)
      // 如果有引用到ending文件夹的资源,则需要把ending文件夹的资源拷贝过来game文件夹下,用于仓库的PlayTurbo展示
      const gameResWhiteList = []

      console.log(performance.now() - start)

      // 编辑器的场景文件 scene_editor.bin
      const meta = {}
      const _uuid = generateUUID()
      meta.uuid = _uuid
      meta.type = AssetType.ASSET_SCENE
      meta.source = ['.state']
      const binContent = JSON.stringify([
        JSON.stringify(meta),
        JSON.stringify(content),
      ])
      const binName = 'scene_editor.bin'
      const path = AssetRoute.SCENE + '/' + binName
      const pathState = AssetRoute.SCENE + '/scene_editor.state'
      const binContentState = JSON.stringify(content)

      console.log(performance.now() - start)

      // 资源白名单,发布时不删除
      // 读取结束页面的白名单,用于合并
      const mediaWhiteList = []

      console.log(performance.now() - start)
      // eslint-disable-next-line space-before-function-paren
      await DFS(world, async function (node) {
        const audio = node.getScript('ps.AudioNode')
        if (audio) {
          mediaWhiteList.push(audio.audioUrl)
        }
        const globalConfigBg = node.getScript('ps.GlobalConfigBg')
        if (globalConfigBg) {
          if (globalConfigBg.bgmAudioUrl) {
            mediaWhiteList.push(globalConfigBg.bgmAudioUrl.replace(/^\//, ''))
          } else {
            mediaWhiteList.push(filePath.BGM_DEFAULT)
          }
        }
        const video = node.getScript('ps.Video')
        if (video) {
          if (video.videoUrl) {
            mediaWhiteList.push(video.videoUrl)
          }
          if (video.audioUrl) {
            mediaWhiteList.push(video.audioUrl)
          }
        }
        // 自研素材的龙骨是两个json,1个bin,1个png,需要把_ske.json那个文件导出
        if (
          node.class === 'qc.Sprite' &&
          node.texture &&
          /tex\.bin$/.test(node.texture.atlas.url)
        ) {
          const url = node.texture.atlas.url.slice(
            node.texture.atlas.url.indexOf('resource'),
          )
          gameResWhiteList.push(url.replace(/tex\.bin$/, 'ske.json'))
        }
        // 多语言图片字记录到资源白名单
        if (isLangImage(node)) {
          const binUrls = node.texture.atlas.url.split('/')
          let binName = binUrls[binUrls.length - 1]
          const imgUrls = node.texture.atlas.img.src.split('/')
          let imgName = imgUrls[imgUrls.length - 1]
          const languageList = store.state.workspace.language.languageList
          languageList.forEach((lang) => {
            binName = file2lang(binName, lang)
            binUrls[binUrls.length - 1] = binName
            const binPath = binUrls.join('/')
            if (!gameResWhiteList.includes(binPath)) {
              gameResWhiteList.push(binPath)
            }
            imgName = file2lang(imgName, lang)
            binUrls[binUrls.length - 1] = imgName
            const imgPath = binUrls.join('/')
            if (!gameResWhiteList.includes(imgPath)) {
              gameResWhiteList.push(imgPath)
            }
          })
        }
        if (isSequenceFrame(node)) {
          const curFrame = getCurrentActionBinFiles(node)
          const frames = curFrame.split(',')
          frames.forEach((frame) => {
            frame = frame.replace(/\.\//g, '')
            if (!gameResWhiteList.includes(frame)) {
              gameResWhiteList.push(frame)
            }
            frame = frame.replace(/\.bin$/, '.png')
            if (!gameResWhiteList.includes(frame)) {
              gameResWhiteList.push(frame)
            }
          })
        }
      })

      const editorConfig = store.state.workspace.editorConfig
      // 自研素材因为无法添加音效依赖,所以音效全导出
      if (!store.state.workspace.project.isNormMaterial) {
        let audioList = Object.values(state.uuid2url).filter((path) => {
          if (/(\.mp3\.bin|\.mp3)$/i.test(path)) {
            return true
          }
        })
        // 如果有背景音乐，则加入背景音乐
        if (
          !audioList.find((item) => item === getBgmAudioUrl()) &&
          _.get(editorConfig, 'bgm.value') === 1
        ) {
          audioList.push(getBgmAudioUrl())
        }
        audioList = audioList.map((audio) => {
          return audio.replace(/\.bin$/, '').replace(/^\//, '')
        })
        mediaWhiteList.push(...audioList)
      }
      // 基础包的背景图片，当时一个qc.Image时，如果改image被删除了，也不会在dependences中
      const bgConfig = bgScript.getScript(state.gamePlay, false)
      if (
        bgConfig &&
        bgConfig.bgUrl &&
        !mediaWhiteList.find((item) => item === bgConfig.bgUrl) &&
        _.get(editorConfig, 'bg.value') === 1
      ) {
        mediaWhiteList.push(bgConfig.bgUrl)
      }

      console.log(performance.now() - start)

      const finalRes = gameResWhiteList
      finalRes.push(...mediaWhiteList)
      necessaryRes.forEach((res) => {
        if (finalRes.indexOf(res) === -1) {
          finalRes.push(res)
        }
      })
      await fileSave({
        projectKey: projectKey,
        path: AssetRoute.WHITE_LIST_RES,
        binContent: finalRes.join('\n'),
      })

      console.log(performance.now() - start)

      const pRequest = []
      const p1 = new Promise((resolve, reject) => {
        fileSave({ projectKey, path, binContent }).then((res) => {
          resolve(res)
        })
      })
      pRequest.push(p1)
      const p2 = new Promise((resolve, reject) => {
        fileSave({
          projectKey,
          path: pathState,
          binContent: binContentState,
        }).then((res) => {
          resolve(res)
        })
      })
      pRequest.push(p2)
      // 基础包形式的项目每次保存时需要更新资源依赖数
      if (!store.state.workspace.project.isNormMaterial) {
        let loadingJsContent = await readFile({
          pid: projectKey,
          path: 'lib/qc-loading-debug.js',
        })
        let assetCount = 1
        for (const key in content.dependences) {
          if (key !== '__builtin_resource__') {
            assetCount += 1
          }
        }
        loadingJsContent = loadingJsContent.replace(
          /qici.loadingAssetCount\s*?=\s*?(\d+);/,
          `qici.loadingAssetCount = ${assetCount};`,
        )
        pRequest.push(
          new Promise((resolve) => {
            fileSave({
              projectKey,
              path: 'lib/qc-loading-debug.js',
              binContent: loadingJsContent,
            }).then((res) => {
              resolve(res)
            })
          }),
        )
      }
      if (hasSelfDevPermission() && hasCoMerge && hasCoMerge.res === 1) {
        pRequest.push(
          deleteFile({
            pid: projectKey,
            path: 'StartScene_tech.html',
            recursive: 0,
          }),
        )
        pRequest.push(
          deleteFile({
            pid: projectKey,
            path: 'Temp/scene_editor_tech.bin',
            recursive: 0,
          }),
        )
        pRequest.push(
          deleteFile({
            pid: projectKey,
            path: 'Temp/scene_editor_tech.bin.meta',
            recursive: 0,
          }),
        )
        pRequest.push(
          deleteFile({
            pid: projectKey,
            path: 'Temp/scene_editor_tech.state',
            recursive: 0,
          }),
        )
      }
      // 记录每个节点对应的资源
      const p3 = new Promise((resolve, reject) => {
        fileSave({
          projectKey,
          path: AssetRoute.NODE_UUID2RES,
          binContent: JSON.stringify(nodeUuid2Res, null, 2),
        }).then((res) => {
          resolve(res)
        })
      })
      pRequest.push(p3)

      // 互动视频编辑器脑图统计数据文件
      if (isIvPage()) {
        const brain = getFinalBrain()
        const p4 = new Promise((resolve, reject) => {
          fileSave({
            projectKey,
            path: AssetRoute.BRAIN,
            binContent: JSON.stringify(brain, null, 2),
          }).then((res) => {
            resolve(res)
          })
        })
        pRequest.push(p4)
      }
      const result = await Promise.all(pRequest)
      // 协同流程项目版本+1
      if (hasSelfDevPermission()) {
        await cooperationUpdateVersion()
      }

      console.log(performance.now() - start)

      return result
    },

    async savePublish({ state }) {
      const qcGame = gameIframe.qcGame
      const world = qcGame.world
      const projectKey = router.currentRoute.query.id

      const dependencesPublish = {}
      const contentPublish = qcGame.serializer.buildBundle(
        world,
        dependencesPublish,
      )
      contentPublish.dependences =
        qcGame.serializer.combineDependence(dependencesPublish)
      // 发布的场景文件
      const metaPublish = {}
      const _uuid = generateUUID()
      metaPublish.uuid = _uuid
      metaPublish.type = AssetType.ASSET_SCENE
      metaPublish.source = ['.state']
      const arrPublish = []
      const gamePlayState = JSON.stringify(contentPublish)
      arrPublish.push(JSON.stringify(metaPublish))
      arrPublish.push(gamePlayState)
      const binContentPublish = JSON.stringify(arrPublish)
      const p1 = new Promise((resolve, reject) => {
        const path = 'resource/scene/gamePlay.state'
        fileSave({ projectKey, path, binContent: gamePlayState }).then(
          (res) => {
            resolve(res)
          },
        )
      })
      const p2 = new Promise((resolve, reject) => {
        const path = 'resource/scene/gamePlay.bin'
        fileSave({ projectKey, path, binContent: binContentPublish }).then(
          (res) => {
            resolve(res)
          },
        )
      })
      const result = await Promise.all([p1, p2])
      return result
    },

    /**
     * 保存文件到远端
     * @param {{}} param0
     * @param {{params: {pid: string, path: string}, data: string}} param1
     */
    saveFile({ state }, { params, data }) {
      return saveFile(params, data)
    },

    resetState({ state }) {
      state.data = null
      state.activeNode = null
      state.uuid2url = null
      state.currentPage = GamePage.GAME
      state.game = null
      state.iframeWindow = null
      state.activeNodeList = []
    },

    ...actions,
  },

  modules: {
    prefab,
    screenAdapter,
    device,
    scriptManager,
    sequenceManager,
    sceneManager,
    visualProgram,
    templateVersion,
    video,
    audio,
    bgm,
    takeColor,
    drawLine,
    erase,
  },
}

export default game
